import { BulkRequest } from "components/PaymentsLanding/Tabs/BulkRequest/BulkRequest";
import { balanceAges } from "components/PaymentsLanding/Tabs/BulkRequest/balanceMap";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { showPatientActivityBulkHistoryModal } from "components/PaymentsLanding/Tabs/PatientActivity/PatientActivityBulkHistoryModal/showPatientActivityBulkHistoryModal";
import { toReadonlySet } from "utils/sets";
import { useHeaderDates } from "molecules/filters/rangePicker/datePickerContext";
import type { BalanceAge } from "api/responses";

interface BulkRequestSearchParamsBase {
    readonly minBalanceEnabled: boolean;
    readonly minBalance: number | null;
    readonly patient: string;
    readonly excludeOutstandingClaims: boolean;
    readonly excludeActivePaymentPlans: boolean;
}

export interface BulkRequestSearchParams extends BulkRequestSearchParamsBase {
    readonly agings: ReadonlySet<BalanceAge>;
}

export interface BulkRequestSearchParamsQuery extends BulkRequestSearchParamsBase {
    readonly agings: BalanceAge[];
}

type BulkRequestSearchParamsPartial = Partial<BulkRequestSearchParamsQuery>;

export const Route = createFileRoute("/history/bulk-request")({
    component: BulkRequestRoute,
    validateSearch: (search: BulkRequestSearchParamsPartial): BulkRequestSearchParamsPartial => {
        // Convert agings string array to BalanceAge array, defaulting to all if not set
        const agingsArray = Array.isArray(search.agings) && search.agings.length > 0 ? search.agings : balanceAges;

        return {
            agings: agingsArray as BalanceAge[],
            minBalanceEnabled: search.minBalanceEnabled === true,
            minBalance: typeof search.minBalance === "number" ? search.minBalance : null,
            patient: typeof search.patient === "string" ? search.patient : "",
            excludeOutstandingClaims: search.excludeOutstandingClaims !== false,
            excludeActivePaymentPlans: search.excludeActivePaymentPlans !== false,
        } satisfies BulkRequestSearchParamsQuery;
    },
});

function BulkRequestRoute() {
    const searchParams = Route.useSearch() satisfies BulkRequestSearchParams;
    const [start, end] = useHeaderDates();
    const navigate = useNavigate();

    const onBulkRequestSubmitted = () => {
        void navigate({
            to: "/history/patients",
            search: {},
        });

        void showPatientActivityBulkHistoryModal();
    };

    return start && end ? (
        <BulkRequest
            enabled={true}
            agings={toReadonlySet(searchParams.agings)}
            minBalanceEnabled={searchParams.minBalanceEnabled}
            minBalance={searchParams.minBalance}
            patient={searchParams.patient}
            excludeOutstandingClaims={searchParams.excludeOutstandingClaims}
            excludeActivePaymentPlans={searchParams.excludeActivePaymentPlans}
            onSubmitted={onBulkRequestSubmitted}
        />
    ) : null;
}
